import type { JwtPayload, User } from '@supabase/supabase-js'
import {
  retryOnNetworkThrow,
  retryOnSupabaseNetworkError,
  isTransientNetworkError,
} from '@/lib/supabase/retry'
import { getSeededJwks } from '@/lib/supabase/jwks'

/**
 * Local JWT verification helpers — the replacement for hot-path
 * `supabase.auth.getUser()` calls.
 *
 * WHY: `getUser()` is a NETWORK round-trip to GoTrue's `/auth/v1/user` on
 * every call. The proxy gate, the admin layout, most admin pages, all 59
 * `/api/admin/*` guards, and every public/member/viewer layout each made
 * one — a single admin visiting the dashboard generated 70–100 GoTrue
 * calls in seconds (sidebar prefetch + tab fan-out), which tripped
 * Supabase's per-IP auth rate limit and bounced admins to /login
 * mid-session (verified burst: 2026-08-17, 320 errors/min).
 *
 * HOW: this project signs access tokens with an asymmetric ES256 key
 * (JWKS at /auth/v1/.well-known/jwks.json). `supabase.auth.getClaims()`
 * verifies the token's signature + expiry LOCALLY via WebCrypto, caching
 * the JWKS per isolate — zero GoTrue traffic on the hot path. The only
 * network call left is the hourly session refresh when the access token
 * actually expires (which `getSession()` inside `getClaims()` performs,
 * and which the proxy client needed to do anyway to rotate cookies).
 *
 * SECURITY TRADE-OFF (deliberate, standard for JWT systems): local
 * verification trusts the token for its remaining lifetime. Sign-out,
 * bans, and role changes take effect at the next token refresh (access
 * token TTL, ≤1 hour) instead of instantly. Endpoints that perform
 * irreversible/sensitive actions get instant-revocation semantics via
 * `requireStaff({ revalidate: true })` (one network getUser after the
 * local gate) — see require-role.ts; all irreversible admin routes set it.
 *
 * The claims payload carries everything the gates need: `sub`, `email`,
 * `aal`, `session_id`, `app_metadata` (incl. the auth-hook-stamped
 * `role` / `has_access`), and `user_metadata`.
 */

/** Minimal client surface — any supabase client with an auth namespace.
 *
 * `getClaims` is declared with METHOD syntax (not an arrow property) on purpose:
 * method params are checked bivariantly, so the real supabase client — whose
 * `getClaims` options also carry `keys`/`allowExpired` typed with auth-js's
 * internal `JWK` — stays assignable to this narrower surface. */
type GetClaimsOptions = { jwks?: { keys: JsonWebKey[] }; allowExpired?: boolean }
type ClaimsClient = {
  auth: {
    getClaims(
      jwt?: string,
      options?: GetClaimsOptions
    ): Promise<{ data: { claims: JwtPayload } | null; error: unknown }>
  }
}

/**
 * Outcome of a claims verification, distinguishing the two very different
 * reasons claims can be absent:
 *
 *  - `absent`        — we reached the auth endpoint and it gave a definitive
 *                      answer: there is no valid session (no token, invalid, or
 *                      expired with an unusable refresh token). Safe to treat as
 *                      logged-out → a protected route SHOULD redirect to /auth.
 *  - `indeterminate` — we could NOT get an answer: the auth lookup failed with a
 *                      transient network fault (JWKS fetch / session-refresh
 *                      socket reset, or the proxy client's AbortSignal timeout).
 *                      This is NOT evidence of being logged out, so a protected
 *                      route should avoid ejecting a possibly-authenticated user.
 *
 * Collapsing `indeterminate` into "logged out" is what bounces real, signed-in
 * members to /auth during a Supabase-gateway slow spell (the dominant
 * `/middleware` error class in the Vercel logs).
 */
export type ClaimsResolution =
  | { claims: JwtPayload; reason: 'ok' }
  | { claims: null; reason: 'absent' }
  | { claims: null; reason: 'indeterminate' }

/**
 * Verify the current session's access token locally and return a
 * {@link ClaimsResolution} that distinguishes "no session" from "could not
 * determine". Never throws.
 *
 * Retries once on transient network faults — both error-returning
 * (Supabase-style `{ error }`) and throwing (JWKS fetch / session refresh TLS
 * resets) — before concluding anything, so a single blip doesn't decide the
 * outcome. A fault that survives the retries yields `indeterminate`, never
 * `absent`.
 */
export async function resolveVerifiedClaims(
  supabase: ClaimsClient,
  // When provided, verify THIS access token locally and do NOT let auth-js call
  // getSession() (which is what triggers the token-refresh POST). Passing the
  // current, still-valid token turns this into a pure local ES256 verify with
  // zero GoTrue traffic — the fix for the concurrent-refresh stampede where a
  // single page's layout + i18n + page each refreshed the same near-expiry
  // session at once (Supabase ticket SU-494028). Callers that omit it keep the
  // original refresh-capable behaviour (the proxy — the one place that rotates
  // cookies — and the expired/missing-token fallback). A server-only module
  // (server-claims.ts) reads the token from the cookie and passes it here;
  // claims.ts itself stays client-safe (no next/headers import).
  accessToken?: string
): Promise<ClaimsResolution> {
  // Verify the token signature against a JWKS sourced OFF the custom-domain
  // gateway when one is available (see lib/supabase/jwks.ts). When it is not,
  // `jwks` is undefined and getClaims falls back to its default fetch — no
  // behaviour change. The ~hourly session refresh inside getClaims() is
  // unaffected (it still goes to the gateway, bounded by the proxy per-attempt
  // retry).
  const jwks = await getSeededJwks()
  const call = (useSeed: boolean) =>
    retryOnNetworkThrow(() =>
      retryOnSupabaseNetworkError(() =>
        supabase.auth.getClaims(accessToken, useSeed && jwks ? { jwks } : undefined)
      )
    )
  try {
    let { data, error } = await call(true)
    // Guard against a BAD SEED (e.g. a corrupted env JWKS whose kid matches the
    // token's but whose key material is wrong): that yields a signature error,
    // NOT a network error, which would otherwise fall through to 'absent' and
    // silently log out EVERY signed-in member. If a seed was used and produced a
    // definitive (non-network) verification failure, re-verify ONCE against the
    // default gateway JWKS before concluding the session is really absent.
    if (jwks && !data?.claims && error && !isTransientNetworkError(error)) {
      ;({ data, error } = await call(false))
    }
    if (data?.claims) return { claims: data.claims, reason: 'ok' }
    // No claims. A returned (non-throwing) transient network error means we
    // never got a definitive answer — don't collapse it into "logged out".
    if (error && isTransientNetworkError(error)) {
      return { claims: null, reason: 'indeterminate' }
    }
    return { claims: null, reason: 'absent' }
  } catch {
    // Thrown after retries — JWKS fetch / session-refresh TLS reset, or the
    // proxy client's AbortSignal.timeout. Could not determine the session.
    return { claims: null, reason: 'indeterminate' }
  }
}

/**
 * Verify the current session's access token locally and return its
 * claims, or null when there is no (valid) session. Never throws.
 *
 * Thin wrapper over {@link resolveVerifiedClaims} for the many callers that
 * only need "claims or not". Callers on protected routes that must not eject a
 * user on a transient fault should use `resolveVerifiedClaims` and branch on
 * `reason` instead.
 */
export async function getVerifiedClaims(
  supabase: ClaimsClient
): Promise<JwtPayload | null> {
  return (await resolveVerifiedClaims(supabase)).claims
}

/**
 * Verify a caller-supplied Bearer token locally and return its claims,
 * or null when invalid/expired. Used by the mobile (Bearer) auth path.
 */
export async function getVerifiedBearerClaims(
  supabase: ClaimsClient,
  jwt: string
): Promise<JwtPayload | null> {
  const jwks = await getSeededJwks()
  try {
    const { data } = await retryOnNetworkThrow(() =>
      retryOnSupabaseNetworkError(() =>
        supabase.auth.getClaims(jwt, jwks ? { jwks } : undefined)
      )
    )
    return data?.claims ?? null
  } catch {
    return null
  }
}

/** The auth-hook-stamped role claim (`app_metadata.role`), if any. */
export function claimsRole(claims: JwtPayload | null): string | undefined {
  const role = (claims?.app_metadata as Record<string, unknown> | undefined)
    ?.role
  return typeof role === 'string' ? role : undefined
}

/**
 * Shape a verified claims payload into the `User`-like object the
 * SiteHeader (and other presentational consumers) read: id, email, and
 * metadata. Only the fields those consumers actually touch are real;
 * the cast is contained here on purpose — do NOT hand this to code that
 * needs a full GoTrue user record.
 */
export function claimsToUserShape(claims: JwtPayload): User {
  return {
    id: claims.sub,
    email: claims.email as string | undefined,
    app_metadata: (claims.app_metadata as User['app_metadata']) ?? {},
    user_metadata: (claims.user_metadata as User['user_metadata']) ?? {},
    aud: (claims.aud as string) ?? 'authenticated',
    created_at: '',
  } as User
}
